// src/components/ExamSplitScreen.jsx
// The resizable split-screen answer workspace for one question: a
// persistent reference pane on the right, and Exhibits/Requirements/
// Responses on the left. Bottom-right has Previous/Next/Navigate, bottom-left
// has Help/Tax Tables.
//
// Exhibits, Requirements, the Word Processor/Spreadsheet, and the Help/Tax
// Tables document all open as in-page floating panels (ExamFloatingPanel) -
// draggable/resizable chrome layered over this same page, matching how real
// ACCA CBE exam software presents them - NOT browser window.open() popups.
// Panels are scoped to the current question: navigating to a different
// question closes them, since their content (answer text, exhibit list) is
// question-specific.
//
// A "question" here is a group of one or more marking sections (see
// src/utils/examGroups.js) - e.g. Question 2's parts (i) and (ii) share one
// screen, one combined exhibit/requirement list, and one shared Word
// Processor/Spreadsheet response, exactly like the real exam. Marking still
// happens per underlying section - the student just labels which part
// they're answering within their own answer, same as on paper.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clock, Flag, ChevronLeft, ChevronRight, Compass, HelpCircle, FileText, ClipboardList, PenLine, FileSpreadsheet, CheckCircle2, Calculator, NotebookPen, Highlighter, Strikethrough, Ban } from 'lucide-react';
import { getAuthenticatedFileUrl } from '../firebase/submissions';
import ExamFloatingPanel from './ExamFloatingPanel';
import ExamMaterialPanel from './ExamMaterialPanel';
import ExamAnswerPanel from './ExamAnswerPanel';
import ExamCalculatorPanel from './ExamCalculatorPanel';
import ExamScratchPadPanel from './ExamScratchPadPanel';
import { BreakdownBox } from './ExamQuestionBreakdown';
import { calculateGroupMarks, groupExhibits, groupRequirements } from '../utils/examGroups';

function formatCountdown(ms) {
  if (ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function plainTextWordCount(html) {
  const text = (html || '').replace(/<[^>]*>/g, ' ').trim();
  return text ? text.split(/\s+/).length : 0;
}

// New uploads already default to an extension-free title (see
// uploadExamMaterial in firebase/examMaterials.js) - this catches documents
// uploaded before that existed, so students never see ".pdf" in a title.
function displayTitle(title) {
  return (title || '').replace(/\.[^./\\]+$/, '');
}

// Icon-only toolbar buttons (Scratch Pad/Highlight/Strikethrough/Calculator/
// Flag) - a `title` attribute on each carries the label as a hover tooltip
// instead of visible text.
const iconButtonStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: '32px', height: '32px', padding: 0,
  background: 'white', color: '#718096',
  border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer'
};

const ExamSplitScreen = ({
  sessionId, group, sectionStructure, sectionNumber, totalSections, remainingMs, unlimitedTime,
  clockStarting, timerError,
  answer, scratchPad, scratchPadUpdatedAt, exhibitMarks, onToggleFlag,
  onNext, onPrevious, onNavigate, submitting, helpDocumentUrl, showHelpButton = true
}) => {
  const [leftWidthPct, setLeftWidthPct] = useState(33);
  const [materialUrls, setMaterialUrls] = useState({}); // { [itemId]: downloadUrl }
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const [panels, setPanels] = useState([]); // { id, kind, title, item?, mode?, defaultPosition, defaultSize }
  const [zIndices, setZIndices] = useState({}); // { [panelId]: zIndex }
  const nextZRef = useRef(1);
  const openOffsetRef = useRef(0);
  const materialPanelRefs = useRef({}); // panelId -> ExamMaterialPanel imperative handle
  const [showHighlightColors, setShowHighlightColors] = useState(false);
  const highlightDropdownRef = useRef(null);
  const HIGHLIGHT_COLORS = ['#fff59d', '#a5d6a7', '#f8bbd0', '#90caf9']; // yellow/green/pink/blue - same set the real tool offers

  useEffect(() => {
    if (!showHighlightColors) return;
    const handleOutsideClick = (e) => {
      if (highlightDropdownRef.current && !highlightDropdownRef.current.contains(e.target)) {
        setShowHighlightColors(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showHighlightColors]);

  const isUrgent = remainingMs > 0 && remainingMs < 5 * 60 * 1000;
  const isLastSection = sectionNumber === totalSections;
  const totalMarks = calculateGroupMarks(group, sectionStructure);
  const isMultiPart = group.sections.length > 1;
  const hasGroupBreakdown = isMultiPart && !!(group.breakdown && group.breakdown.trim());

  const handleDragStart = () => { draggingRef.current = true; };
  const handleDragEnd = () => { draggingRef.current = false; };
  const handleDragMove = useCallback((e) => {
    if (!draggingRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setLeftWidthPct(Math.min(75, Math.max(25, pct)));
  }, []);

  const exhibits = groupExhibits(group);
  const requirements = groupRequirements(group);

  // Resolve exhibit/requirement Storage paths to authenticated download URLs
  // whenever the question changes
  useEffect(() => {
    let cancelled = false;
    const items = [...exhibits, ...requirements];
    items.forEach(async (item) => {
      if (!item.filePath) return;
      const url = await getAuthenticatedFileUrl(item.filePath);
      if (!cancelled && url) {
        setMaterialUrls(prev => ({ ...prev, [item.id]: url }));
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  // Panels are question-specific (exhibits, answer text). Moving to a new
  // question closes whatever was open, same as real exam software returning
  // to a fresh question screen.
  useEffect(() => {
    setPanels([]);
    setZIndices({});
  }, [group.id]);

  const isTextMaterial = (item) => item.type === 'text' || !item.filePath;

  const bringToFront = (id) => {
    nextZRef.current += 1;
    setZIndices(prev => ({ ...prev, [id]: nextZRef.current }));
  };

  const openPanel = (id, panelData) => {
    if (panels.some(p => p.id === id)) {
      bringToFront(id);
      return;
    }
    const offset = openOffsetRef.current % 6;
    openOffsetRef.current += 1;
    nextZRef.current += 1;
    setZIndices(z => ({ ...z, [id]: nextZRef.current }));
    setPanels(prev => [...prev, {
      id,
      defaultPosition: { x: 100 + offset * 30, y: 80 + offset * 26 },
      defaultSize: { width: 420, height: 380 },
      ...panelData
    }]);
  };

  const closePanel = (id) => {
    setPanels(prev => prev.filter(p => p.id !== id));
    setZIndices(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const openMaterial = (item) => {
    const ready = isTextMaterial(item) || !!materialUrls[item.id];
    if (!ready) return;
    openPanel(`material_${item.id}`, {
      kind: 'material',
      title: displayTitle(item.title),
      item
    });
  };

  const wordCount = plainTextWordCount(answer?.wordProcessor?.html);
  const hasSpreadsheetContent = !!answer?.spreadsheet?.gridJson;
  // A second, always-blank slot only exists once the head marker turns on
  // "Include blank spreadsheet/word processor as well" for this question
  // (see functions/index.js's initialAnswers seeding) - its mere presence
  // here is what decides whether the second Responses button/panel shows
  // at all, and whether the primary one is labelled plain or "Prepopulated
  // ..." (same rule generateExamPdf.js uses).
  const hasBlankSpreadsheet = answer?.blankSpreadsheet !== undefined;
  const hasBlankSpreadsheetContent = !!answer?.blankSpreadsheet?.gridJson;
  const spreadsheetLabel = hasBlankSpreadsheet ? 'Prepopulated Spreadsheet' : 'Spreadsheet';
  const hasBlankWordProcessor = answer?.blankWordProcessor !== undefined;
  const blankWordCount = plainTextWordCount(answer?.blankWordProcessor?.html);
  const wordProcessorLabel = hasBlankWordProcessor ? 'Prepopulated Word Processor' : 'Word Processor';

  const panelTitleFor = (mode) => {
    if (mode === 'spreadsheet') return spreadsheetLabel;
    if (mode === 'blankspreadsheet') return 'Blank Spreadsheet';
    if (mode === 'blankwordprocessor') return 'Blank Word Processor';
    return wordProcessorLabel;
  };

  const openAnswerPanel = (mode) => {
    // The spreadsheet needs more room than the word processor by default -
    // at 560x460 even a handful of columns force horizontal scrolling before
    // the student types anything, which is the opposite of what a real
    // spreadsheet feels like. The panel is still freely resizable either way.
    const defaultSize = (mode === 'spreadsheet' || mode === 'blankspreadsheet') ? { width: 900, height: 560 } : { width: 560, height: 460 };
    openPanel(`answer_${group.id}_${mode}`, {
      kind: 'answer',
      title: `${panelTitleFor(mode)} - ${group.name}`,
      mode,
      defaultSize,
      trackViewport: true
    });
  };

  const openHelpPanel = () => {
    if (!helpDocumentUrl) {
      alert('No Help / Tax Tables document has been uploaded yet.');
      return;
    }
    openPanel('help_document', {
      kind: 'help',
      title: 'Help / Tax Tables',
      defaultSize: { width: 480, height: 520 }
    });
  };

  const openCalculatorPanel = () => {
    openPanel('calculator', {
      kind: 'calculator',
      title: 'Calculator',
      defaultSize: { width: 320, height: 460 }
    });
  };

  // Keyed on sessionId, not group.id - unlike every other panel here, the
  // scratch pad isn't question-specific, so it deliberately still closes
  // when the question changes (same panel-lifecycle effect as everything
  // else) but its content is the same no matter which question reopens it.
  const openScratchPadPanel = () => {
    openPanel('scratchpad', {
      kind: 'scratchpad',
      title: 'Scratch Pad',
      defaultSize: { width: 420, height: 480 }
    });
  };

  // Highlight/Strikethrough live as persistent buttons here (beside Scratch
  // Pad), matching the real exam software's own toolbar, rather than only
  // appearing after a selection is made - they act on whatever text is
  // currently selected in the frontmost open document panel. Several
  // exhibits/requirements can be open at once, so "frontmost" (highest
  // z-index) is the same notion of "the one you're looking at" the panels'
  // own click-to-focus behaviour already uses.
  const getFrontmostMaterialPanelHandle = () => {
    const materialPanels = panels.filter(p => p.kind === 'material' && materialPanelRefs.current[p.id]?.supportsMarking);
    if (!materialPanels.length) return null;
    const frontmost = materialPanels.reduce((best, p) =>
      (zIndices[p.id] || 0) > (zIndices[best.id] || 0) ? p : best
    );
    return materialPanelRefs.current[frontmost.id];
  };

  const applyToFrontmostDocument = (action) => {
    const handle = getFrontmostMaterialPanelHandle();
    if (!handle) {
      alert('Open an exhibit or requirement document first, then select some text to highlight or strike through.');
      return;
    }
    const applied = action(handle);
    if (!applied) {
      alert('Select some text in the document first, then click Highlight or Strikethrough.');
    }
  };

  const handleHighlightColor = (color) => {
    setShowHighlightColors(false);
    applyToFrontmostDocument((handle) => handle.applyHighlight(color));
  };

  const handleRemoveHighlight = () => {
    setShowHighlightColors(false);
    applyToFrontmostDocument((handle) => handle.removeHighlight());
  };

  const handleStrikethrough = () => {
    applyToFrontmostDocument((handle) => handle.applyStrikethrough());
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
    >
      <div style={{
        background: '#1a2332', color: 'white', padding: '14px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <div style={{ fontSize: '11px', letterSpacing: '1px', color: '#8b96a8', fontWeight: '700' }}>
            QUESTION {sectionNumber} OF {totalSections}
          </div>
          <div style={{ fontSize: '15px', fontWeight: '600' }}>
            {group.name}{totalMarks ? ` (${totalMarks} marks)` : ''}
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 16px', borderRadius: '6px',
          background: isUrgent ? '#e74c3c' : 'rgba(255,255,255,0.1)',
          fontWeight: '700', fontSize: unlimitedTime ? '13px' : '18px', fontVariantNumeric: 'tabular-nums'
        }}>
          <Clock size={18} />
          {unlimitedTime ? 'No time limit' : clockStarting ? 'Starting timer...' : formatCountdown(remainingMs)}
        </div>
      </div>

      {timerError && (
        <div style={{ padding: '10px 24px', background: '#fee2e2', color: '#991b1b', fontSize: '13px' }}>
          {timerError}
        </div>
      )}

      <div ref={containerRef} style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left pane: Documents / Requirements / Responses */}
        <div style={{ width: `${leftWidthPct}%`, overflowY: 'auto', padding: '20px', background: '#f4f6f9' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
            <button type="button" onClick={openScratchPadPanel} title="Scratch Pad" style={iconButtonStyle}>
              <NotebookPen size={16} />
            </button>
            <div ref={highlightDropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowHighlightColors(v => !v)}
                title="Highlight"
                style={iconButtonStyle}
              >
                <Highlighter size={16} />
              </button>
              {showHighlightColors && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 20,
                  display: 'flex', gap: '4px', padding: '6px', background: '#2c3e50',
                  borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.3)'
                }}>
                  {HIGHLIGHT_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      title="Highlight"
                      onClick={() => handleHighlightColor(color)}
                      style={{ width: '20px', height: '20px', borderRadius: '4px', background: color, border: '1px solid rgba(255,255,255,0.4)', cursor: 'pointer' }}
                    />
                  ))}
                  <button
                    type="button"
                    title="Remove highlight"
                    onClick={handleRemoveHighlight}
                    style={{
                      width: '20px', height: '20px', borderRadius: '4px', background: 'white',
                      border: '1px solid rgba(255,255,255,0.4)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c33329'
                    }}
                  >
                    <Ban size={13} />
                  </button>
                </div>
              )}
            </div>
            <button type="button" onClick={handleStrikethrough} title="Strikethrough" style={iconButtonStyle}>
              <Strikethrough size={16} />
            </button>
            <button type="button" onClick={openCalculatorPanel} title="Calculator" style={iconButtonStyle}>
              <Calculator size={16} />
            </button>
            <button
              type="button"
              onClick={onToggleFlag}
              title={answer?.flagged ? 'Flagged for review' : 'Flag for review'}
              style={{
                ...iconButtonStyle,
                background: answer?.flagged ? '#fee2e2' : 'white',
                color: answer?.flagged ? '#e74c3c' : '#718096',
                border: `1px solid ${answer?.flagged ? '#fca5a5' : '#e2e8f0'}`
              }}
            >
              <Flag size={16} fill={answer?.flagged ? '#e74c3c' : 'none'} />
            </button>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#8b96a8', letterSpacing: '1px', marginBottom: '8px' }}>
              <FileText size={13} /> DOCUMENTS
            </div>
            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
              {exhibits.length === 0 ? (
                <div style={{ color: '#a0aec0', fontSize: '13px' }}>No documents for this question.</div>
              ) : (
                exhibits.map(ex => {
                  const ready = isTextMaterial(ex) || !!materialUrls[ex.id];
                  return (
                    <button
                      key={ex.id}
                      type="button"
                      onClick={() => openMaterial(ex)}
                      disabled={!ready}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '6px 0',
                        background: 'none', border: 'none',
                        color: ready ? '#667eea' : '#a0aec0',
                        fontSize: '13px', cursor: ready ? 'pointer' : 'default'
                      }}
                    >
                      {displayTitle(ex.title)}{!ready ? ' (loading...)' : ''}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#8b96a8', letterSpacing: '1px', marginBottom: '8px' }}>
              <ClipboardList size={13} /> REQUIREMENTS
            </div>
            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
              {requirements.length === 0 ? (
                <div style={{ color: '#a0aec0', fontSize: '13px' }}>No requirement documents for this question.</div>
              ) : (
                requirements.map(req => {
                  const ready = isTextMaterial(req) || !!materialUrls[req.id];
                  return (
                    <button
                      key={req.id}
                      type="button"
                      onClick={() => openMaterial(req)}
                      disabled={!ready}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '6px 0',
                        background: 'none', border: 'none',
                        color: ready ? '#667eea' : '#a0aec0',
                        fontSize: '13px', cursor: ready ? 'pointer' : 'default'
                      }}
                    >
                      {displayTitle(req.title)}{!ready ? ' (loading...)' : ''}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#8b96a8', letterSpacing: '1px', marginBottom: '8px' }}>
              <PenLine size={13} /> RESPONSES
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              <button
                type="button"
                onClick={() => openAnswerPanel('wordprocessor')}
                disabled={submitting}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                  padding: '14px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer', textAlign: 'left'
                }}
              >
                <PenLine size={18} color="#667eea" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>{wordProcessorLabel}</div>
                  <div style={{ fontSize: '12px', color: '#95a5a6' }}>
                    {wordCount > 0 ? `${wordCount} word${wordCount !== 1 ? 's' : ''} written` : 'Not started'}
                  </div>
                </div>
                {wordCount > 0 && <CheckCircle2 size={16} color="#1b998b" />}
              </button>

              {hasBlankWordProcessor && (
                <button
                  type="button"
                  onClick={() => openAnswerPanel('blankwordprocessor')}
                  disabled={submitting}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '14px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer', textAlign: 'left'
                  }}
                >
                  <PenLine size={18} color="#667eea" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>Blank Word Processor</div>
                    <div style={{ fontSize: '12px', color: '#95a5a6' }}>
                      {blankWordCount > 0 ? `${blankWordCount} word${blankWordCount !== 1 ? 's' : ''} written` : 'Not started'}
                    </div>
                  </div>
                  {blankWordCount > 0 && <CheckCircle2 size={16} color="#1b998b" />}
                </button>
              )}

              <button
                type="button"
                onClick={() => openAnswerPanel('spreadsheet')}
                disabled={submitting}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                  padding: '14px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer', textAlign: 'left'
                }}
              >
                <FileSpreadsheet size={18} color="#1b998b" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>{spreadsheetLabel}</div>
                  <div style={{ fontSize: '12px', color: '#95a5a6' }}>
                    {hasSpreadsheetContent ? 'In progress' : 'Not started'}
                  </div>
                </div>
                {hasSpreadsheetContent && <CheckCircle2 size={16} color="#1b998b" />}
              </button>

              {hasBlankSpreadsheet && (
                <button
                  type="button"
                  onClick={() => openAnswerPanel('blankspreadsheet')}
                  disabled={submitting}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '14px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer', textAlign: 'left'
                  }}
                >
                  <FileSpreadsheet size={18} color="#1b998b" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>Blank Spreadsheet</div>
                    <div style={{ fontSize: '12px', color: '#95a5a6' }}>
                      {hasBlankSpreadsheetContent ? 'In progress' : 'Not started'}
                    </div>
                  </div>
                  {hasBlankSpreadsheetContent && <CheckCircle2 size={16} color="#1b998b" />}
                </button>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#a0aec0', marginTop: '8px' }}>
              Both open in a resizable panel on this page. Your work saves regularly and automatically every time you close one of the response option windows - you can close them and open them anytime.
            </div>
          </div>
        </div>

        {/* Draggable divider */}
        <div
          onMouseDown={handleDragStart}
          style={{ width: '6px', cursor: 'col-resize', background: '#d1d5db', flexShrink: 0 }}
          title="Drag to resize"
        />

        {/* Right pane: persistent question reference */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: 'white' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#8b96a8', letterSpacing: '1px', marginBottom: '8px' }}>
            QUESTION INFORMATION
          </div>
          {hasGroupBreakdown ? (
            <BreakdownBox text={group.breakdown} />
          ) : isMultiPart ? (
            <div style={{ display: 'grid', gap: '16px' }}>
              {group.sections.map(s => {
                // A part's own mark total only makes sense outside a shared
                // technical/professional group - inside one, the cap is
                // shared across parts, so no single number is correct here.
                const inTechGroup = (sectionStructure?.technicalMarkGroups || []).some(g => g.sections.includes(s.id));
                const inProfGroup = (sectionStructure?.professionalMarkGroups || []).some(g => g.sections.includes(s.id));
                const partMarks = (inTechGroup || inProfGroup) ? null
                  : (s.maxTechnical ?? s.maxMark ?? 0) + (s.maxProfessional ?? 1);
                return (
                  <div key={s.id}>
                    <div style={{ fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>
                      {s.name}{partMarks ? ` (${partMarks} marks)` : ''}
                    </div>
                    <BreakdownBox text={s.breakdown} fallback="No information has been provided for this part." />
                  </div>
                );
              })}
            </div>
          ) : (
            <BreakdownBox
              text={group.sections[0].breakdown}
              fallback="No information has been provided for this question."
            />
          )}
        </div>
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 24px', background: '#f4f6f9', borderTop: '1px solid #e2e8f0'
      }}>
        {showHelpButton ? (
          <button
            type="button"
            onClick={openHelpPanel}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 16px', background: 'white', color: '#4a5568',
              border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
            }}
          >
            <HelpCircle size={16} />
            Help / Tax Tables
          </button>
        ) : <span />}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={onPrevious}
            disabled={!onPrevious || submitting}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px', background: 'white', color: '#4a5568',
              border: '2px solid #e2e8f0', borderRadius: '8px',
              cursor: (!onPrevious || submitting) ? 'not-allowed' : 'pointer',
              fontWeight: '600', opacity: onPrevious ? 1 : 0.5
            }}
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <button
            type="button"
            onClick={onNavigate}
            disabled={submitting}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px', background: 'white', color: '#4a5568',
              border: '2px solid #e2e8f0', borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600'
            }}
          >
            <Compass size={16} /> Navigate
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={submitting}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px',
              background: '#667eea', color: 'white', border: 'none', borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '700'
            }}
          >
            {isLastSection ? 'Finish' : 'Next'} <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {panels.map(panel => (
        <ExamFloatingPanel
          key={panel.id}
          title={panel.title}
          onClose={() => closePanel(panel.id)}
          defaultPosition={panel.defaultPosition}
          defaultSize={panel.defaultSize}
          trackViewport={panel.trackViewport}
          zIndex={zIndices[panel.id] || 1}
          onFocus={() => bringToFront(panel.id)}
        >
          {panel.kind === 'material' && (
            <ExamMaterialPanel
              ref={(el) => { if (el) materialPanelRefs.current[panel.id] = el; else delete materialPanelRefs.current[panel.id]; }}
              text={isTextMaterial(panel.item) ? (panel.item.content || '') : null}
              pdfUrl={isTextMaterial(panel.item) ? null : materialUrls[panel.item.id]}
              sessionId={isTextMaterial(panel.item) ? undefined : sessionId}
              materialId={isTextMaterial(panel.item) ? undefined : panel.item.id}
              initialMarks={exhibitMarks?.[panel.item.id]}
            />
          )}
          {panel.kind === 'answer' && (() => {
            const savedSubfield = panel.mode === 'spreadsheet' ? answer?.spreadsheet
              : panel.mode === 'blankspreadsheet' ? answer?.blankSpreadsheet
                : panel.mode === 'blankwordprocessor' ? answer?.blankWordProcessor
                  : answer?.wordProcessor;
            return (
              <ExamAnswerPanel
                sessionId={sessionId}
                sectionId={group.id}
                mode={panel.mode}
                initialValue={panel.mode === 'spreadsheet' || panel.mode === 'blankspreadsheet'
                  ? (savedSubfield?.gridJson || '')
                  : (savedSubfield?.html || '')}
                // Reopening a closed panel remounts it - without this it
                // forgets the content was already saved and shows "Not yet
                // saved" until the next autosave, even though nothing has
                // changed since the last one (see ExamAnswerPanel).
                initialSavedAt={savedSubfield?.updatedAt}
                sectionName={group.name}
              />
            );
          })()}
          {panel.kind === 'help' && (
            <ExamMaterialPanel text={null} pdfUrl={helpDocumentUrl} />
          )}
          {panel.kind === 'calculator' && <ExamCalculatorPanel />}
          {panel.kind === 'scratchpad' && <ExamScratchPadPanel sessionId={sessionId} initialValue={scratchPad} initialSavedAt={scratchPadUpdatedAt} />}
        </ExamFloatingPanel>
      ))}
    </div>
  );
};

export default ExamSplitScreen;
